import React, { FC, useCallback, useRef } from 'react';

import { useQuery } from '@apollo/client';
import { Container, styled } from '@mui/material';
import { InstantSearch } from 'react-instantsearch';
import { useLocation, useParams, useNavigate } from 'react-router-dom';

import NotFoundPage from '~/blocks/NotFoundPage/NotFoundPage';
import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import AnimatedSkeletonMultiline from '~/components/AnimatedSkeletonMultiline';
import Breadcrumbs, { Crumb } from '~/components/Breadcrumbs/Breadcrumbs';
import Hero from '~/components/Hero/Hero';
import { CenteredContainer, WrapperWithBorder } from '~/components/Hero/style';
import PreloadImageLink from '~/components/PreloadImageLink/PreloadImageLink';
import RouteWithCode from '~/components/RouteWithCode/RouteWithCode';
import Search from '~/components/Search/Search';
import SeoBlock from '~/components/SeoBlock/SeoBlock';
import { DEFAULT_DISCOVER_URL, FILTERS, RM_PAGES } from '~/const';
import { useDiscoverContext } from '~/context/DiscoverContext';
import { useRmMetadata } from '~/context/RmContext';
import { useSsrContext } from '~/context/SsrContext';
import { GET_EXPERT_BY_SLUG } from '~/graphql/user';
import useContentAvailability, { ContentAvailability } from '~/hooks/useContentAvailability';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import ExpertInfo from '~/pages/DiscoverPage/components/ExpertInfo/ExpertInfo';
import SERPContainer from '~/pages/DiscoverPage/components/SERPContainer/SERPContainer';
import Tabs from '~/pages/DiscoverPage/components/Tabs/Tabs';
import { buildExpertRoute, DISCOVER_EXPERTS_ROUTE, DISCOVER_SEARCH_ROUTE } from '~/routes';
import { css } from '~/styled';
import {
  LARGE_MARGIN_PX,
  MIDDLE_MARGIN_PX,
  SKELETON_TEXT_HEIGHT_PX,
  SKELETON_TEXT_HEIGHT_SMALL_PX,
  SMALL_MARGIN_PX,
  TINY_MARGIN_PX,
} from '~/theme';
import { Maybe, Query, QueryGetFilteredUsersArgs, Tenant } from '~/types';
import { generateSeoTitle } from '~/utils/generateSeoTitle';
import redirectToNewSlug from '~/utils/redirectToNewSlug';
import { toBoolean } from '~/utils/toBoolean';
import { translate } from '~/utils/translate';
import getCanonicalUrl from '~/utils/urlUtils/getCanonicalUrl';

const attributesExcludedFromClear = [FILTERS.Expert];

const getNavigationArray = (
  contentAvailability: ContentAvailability,
  expertName?: string,
): Crumb[] => {
  return [
    {
      title: translate('page.discover.discover'),
      url: DEFAULT_DISCOVER_URL,
    },
    contentAvailability.experts && {
      title: translate('page.discover.experts'),
      url: DISCOVER_EXPERTS_ROUTE,
    },
    {
      title: expertName,
    },
  ].filter(toBoolean<Crumb>);
};

const generateExpertSrcset = (height?) => {
  const locHeight = height < 580 ? 580 : height;
  return [
    { width: 500, quality: 80, focus: 'auto', height: locHeight },
    { width: 800, quality: 80, focus: 'auto', height: locHeight },
    { width: 1100, quality: 80, focus: 'auto', height: locHeight },
    { width: 1200, quality: 80, focus: 'auto', height: locHeight },
    { width: 1920, quality: 80, focus: 'auto', height: locHeight },
  ];
};

interface ExpertPageProps {
  tenant?: Maybe<Tenant>;
  algoliaIndexName: string;
  searchClient: any;
  loadingKey: boolean;
  userLoading: boolean;
}

const ExpertsPage: FC<ExpertPageProps> = ({
  tenant,
  algoliaIndexName,
  searchClient,
  loadingKey,
  userLoading,
}) => {
  const { t } = useTenantTranslation();
  const headerRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { expertSlug } = useParams();
  const { setSearchState } = useDiscoverContext();
  const { redirect } = useSsrContext();
  const contentAvailability = useContentAvailability();

  useRmMetadata({ page: RM_PAGES.ExpertPage });

  const { data, loading } = useQuery<Pick<Query, 'getFilteredUsers'>, QueryGetFilteredUsersArgs>(
    GET_EXPERT_BY_SLUG,
    {
      variables: {
        filter: {
          slug: [expertSlug || ''],
        },
      },
      skip: !expertSlug,
    },
  );

  const expert = data?.getFilteredUsers?.length ? data?.getFilteredUsers[0] : null;

  const navigationArray = getNavigationArray(contentAvailability, expert?.name);

  const imgSrcset = generateExpertSrcset(headerRef?.current?.clientHeight);

  const handleSearch = (val) => {
    setSearchState((prevState) => ({
      ...prevState,
      query: val,
    }));
    navigate(`${DISCOVER_SEARCH_ROUTE}?s=${val}`);
  };

  const onChange = useCallback(
    ({ uiState, setUiState }) => {
      setUiState(uiState);
      setSearchState(uiState[algoliaIndexName]);
    },
    [algoliaIndexName, setSearchState],
  );

  if (expert && expertSlug) {
    redirectToNewSlug(redirect, pathname, expertSlug, expert?.slug);
  }

  if (!expert && !loading) {
    return (
      <RouteWithCode code={404}>
        <NotFoundPage />
      </RouteWithCode>
    );
  }

  return (
    <>
      {expert && (
        <SeoBlock
          data={{
            seo_title: generateSeoTitle(expert?.name, tenant?.title),
            seo_description: expert?.description || '',
            share_image: expert?.avatar,
          }}
          canonicalUrl={getCanonicalUrl(buildExpertRoute(expertSlug), expert?.tenants)}
        />
      )}
      <PreloadImageLink path={expert?.avatar} srcset={imgSrcset} />
      <InstantSearch
        onStateChange={onChange}
        searchClient={searchClient}
        indexName={algoliaIndexName}
      >
        <Hero
          ref={headerRef}
          title={t('page.discover.discover')}
          imageUrl={expert?.avatar || undefined}
          wrapperCss={headerWrapperCss}
          contentWrapperCss={headerContentWrapperCss}
          imageWrapperCss={headerImageWrapperCss}
          imageSrcset={imgSrcset}
        >
          <CenteredContainer>
            <Search
              refine={handleSearch}
              withClear
              searchPlaceholder={t('page.discover.searchAllExperts')}
            />
            <Tabs />
          </CenteredContainer>
          <HeaderInfoContainer>
            {loading ? (
              <>
                <LoadingBreadcrumbs xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />
                <LoadingTitle xs={SKELETON_TEXT_HEIGHT_PX} />
                <LoadingJobTitle xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />
                <LoadingDescription
                  xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
                  lines={4}
                  spacer={TINY_MARGIN_PX}
                />
              </>
            ) : (
              <>
                <Breadcrumbs crumbs={navigationArray} data-testid="expertBreadcrumbs" />
                <ExpertInfo
                  id={expert?.id}
                  name={expert?.name}
                  jobTitle={expert?.job_title}
                  description={expert?.description}
                />
              </>
            )}
          </HeaderInfoContainer>
        </Hero>
        <Container>
          <ExpertInfoContainer>
            {loading ? (
              <>
                <LoadingBreadcrumbs xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />
                <WrapperWithBorder>
                  <LoadingTitle xs={SKELETON_TEXT_HEIGHT_PX} />
                  <LoadingJobTitle xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />
                  <LoadingDescription
                    xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
                    lines={4}
                    spacer={TINY_MARGIN_PX}
                  />
                </WrapperWithBorder>
              </>
            ) : (
              <>
                <Breadcrumbs crumbs={navigationArray} />
                <WrapperWithBorder>
                  <ExpertInfo
                    id={expert?.id}
                    name={expert?.name}
                    jobTitle={expert?.job_title}
                    description={expert?.description}
                  />
                </WrapperWithBorder>
              </>
            )}
          </ExpertInfoContainer>
          <SERPContainer
            linkedInId={tenant?.linkedin_id}
            attributesExcludedFromClear={attributesExcludedFromClear}
            loadingApiKey={loadingKey || loading}
            userLoading={userLoading}
            initialExpert={expert?.name}
          />
        </Container>
      </InstantSearch>
    </>
  );
};

export default ExpertsPage;

const LoadingBreadcrumbs = styled(AnimatedSkeleton)`
  margin-bottom: ${SMALL_MARGIN_PX};
  width: 300px;
  max-width: 100%;
`;
const LoadingTitle = styled(AnimatedSkeleton)`
  width: 250px;
  max-width: 100%;
  margin-bottom: 5px;
`;
const LoadingJobTitle = styled(AnimatedSkeleton)`
  width: 200px;
  max-width: 100%;
`;
const LoadingDescription = styled(AnimatedSkeletonMultiline)`
  margin-top: ${SMALL_MARGIN_PX};
  width: 850px;
  max-width: 100%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-top: ${MIDDLE_MARGIN_PX};
  }
`;
const headerWrapperCss = css`
  margin-bottom: ${LARGE_MARGIN_PX};
`;
const headerContentWrapperCss = css`
  padding-bottom: 0;
`;
const headerImageWrapperCss = ({ theme }) => css`
  display: none;

  ${theme.breakpoints.up('md')} {
    display: block;
  }
`;
const HeaderInfoContainer = styled(Container)`
  display: none;

  &:after {
    position: absolute;
    display: block;
    content: '';
    height: 3px;
    width: calc(100% - 32px);
    background-color: ${({ theme }) => theme.palette.common.violet};
    opacity: 0.3;
    left: 16px;
    bottom: 0;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: block;
    padding-top: ${LARGE_MARGIN_PX};
    padding-bottom: ${MIDDLE_MARGIN_PX};
    position: relative;
  }
`;
const ExpertInfoContainer = styled('div')`
  ${({ theme }) => theme.breakpoints.up('md')} {
    display: none;
  }
`;
